import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader } from '../design';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, CheckCircle, Wrench, ShieldAlert, FileX2, Loader2,
  HeartPulse, RotateCcw, X, History, Receipt, Coins, Activity, Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { SlidersHorizontal } from 'lucide-react';
import { ManualOverrideDialog } from './ManualOverrideDialog';
import { InvoiceReconcilePanel } from './InvoiceReconcilePanel';

const statusTone = (s) => {
  const v = String(s || '').toLowerCase();
  if (v === 'active') return 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30';
  if (v === 'past_due' || v === 'pending') return 'bg-amber-500/15 text-amber-500 border-amber-500/30';
  if (v === 'cancelled' || v === 'suspended') return 'bg-red-500/15 text-red-500 border-red-500/30';
  return 'bg-slate-500/15 text-slate-400 border-slate-500/30';
};

export default function BillingDrift() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [busyTenant, setBusyTenant] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type: 'fix'|'void', row }
  const [voidPreview, setVoidPreview] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  // Billing Health (half-upgraded / corrupt-billing accounts)
  const [integrity, setIntegrity] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(true);
  const [busyHealth, setBusyHealth] = useState(null);
  // Billing timeline (per-tenant merged charges/invoices/credits/status)
  const [tlOpen, setTlOpen] = useState(false);
  const [tlLoading, setTlLoading] = useState(false);
  const [tlData, setTlData] = useState(null);
  const [tlLookup, setTlLookup] = useState('');
  const [tlFilter, setTlFilter] = useState('all');
  const [tlFixing, setTlFixing] = useState(false);
  const [lookupResults, setLookupResults] = useState([]);
  const [ovOpen, setOvOpen] = useState(false);
  // Credit & refund repair
  const [crLookup, setCrLookup] = useState('');
  const [crResults, setCrResults] = useState([]);
  const [crStatus, setCrStatus] = useState(null);
  const [crBusy, setCrBusy] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null);

  // Debounced tenant search (name / email / tenant id / sub id)
  const lastPickedRef = useRef(null);
  useEffect(() => {
    const q = tlLookup.trim();
    if (q.length < 2 || q === lastPickedRef.current) { setLookupResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await governanceAPI.lookupTenants(q);
        // Ignore a response that lands after the input changed or a pick happened
        // (prevents the dropdown reopening under the timeline modal).
        if (!cancelled && q === tlLookup.trim() && q !== lastPickedRef.current) {
          setLookupResults(res.data?.items || []);
        }
      } catch { if (!cancelled) setLookupResults([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [tlLookup]);

  const pickLookup = (tenantId) => {
    lastPickedRef.current = tenantId;
    setLookupResults([]);
    setTlLookup(tenantId);
    openTimeline(tenantId);
  };

  // Credit & refund repair — debounced tenant search
  useEffect(() => {
    const q = crLookup.trim();
    if (q.length < 2 || q === crStatus?.tenant_id) { setCrResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await governanceAPI.lookupTenants(q);
        setCrResults(res.data?.items || []);
      } catch { setCrResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [crLookup, crStatus]);

  const loadCreditStatus = useCallback(async (tenantId) => {
    if (!tenantId) return;
    setCrResults([]);
    setCrStatus(null);
    setReconcilePreview(null);
    try {
      const res = await governanceAPI.getCreditStatus(tenantId);
      setCrStatus(res.data);
      setCrLookup(tenantId);
    } catch (e) {
      toast.error('Failed to load credit status', { description: e?.response?.data?.detail || e.message });
    }
  }, []);

  const zeroOverdraft = async () => {
    if (!crStatus?.tenant_id) return;
    setCrBusy(true);
    try {
      const res = await governanceAPI.adjustCredit(crStatus.tenant_id, {
        mode: 'zero_overdraft', note: 'Overage debt cleared from Billing Drift',
      });
      toast.success(res.data?.changed
        ? `Overdraft cleared → $${res.data.balance_after}`
        : 'Balance was not negative — no change');
      await loadCreditStatus(crStatus.tenant_id);
    } catch (e) {
      toast.error('Adjust failed', { description: e?.response?.data?.detail || e.message });
    } finally { setCrBusy(false); }
  };

  const previewReconcile = async () => {
    if (!crStatus?.tenant_id) return;
    setCrBusy(true);
    try {
      const res = await governanceAPI.reconcileRefunds(crStatus.tenant_id, true);
      setReconcilePreview(res.data);
    } catch (e) {
      toast.error('Scan failed', { description: e?.response?.data?.detail || e.message });
    } finally { setCrBusy(false); }
  };

  const applyReconcile = async () => {
    if (!crStatus?.tenant_id) return;
    setCrBusy(true);
    try {
      const res = await governanceAPI.reconcileRefunds(crStatus.tenant_id, false);
      toast.success('Refunds reconciled', {
        description: `${res.data.missing_refunded || 0} missing refunded · ${res.data.duplicates_reversed || 0} duplicate(s) reversed`,
      });
      setReconcilePreview(null);
      await loadCreditStatus(crStatus.tenant_id);
    } catch (e) {
      toast.error('Reconcile failed', { description: e?.response?.data?.detail || e.message });
    } finally { setCrBusy(false); }
  };

  const openTimeline = useCallback(async (tenantId) => {
    if (!tenantId) return;
    setTlOpen(true);
    setTlLoading(true);
    setTlData(null);
    setTlFilter('all');
    try {
      const res = await governanceAPI.getBillingTimeline(tenantId);
      setTlData(res.data);
    } catch (e) {
      toast.error('Failed to load timeline', { description: e?.response?.data?.detail || e.message });
      setTlOpen(false);
    } finally {
      setTlLoading(false);
    }
  }, []);

  // Resolve whatever is typed (name / login email / sub_… id) to a real tenant
  // BEFORE opening the timeline. Always does a FRESH lookup for the current query
  // (never trusts the debounced lookupResults, which can be stale and open the
  // wrong tenant). Without this, typing an email passed the raw string as a
  // tenant_id and the backend returned an empty "Unnamed" shell.
  const viewTimelineFromSearch = useCallback(async () => {
    const q = tlLookup.trim();
    if (!q) return;
    try {
      const res = await governanceAPI.lookupTenants(q);
      const items = res.data?.items || [];
      if (items.length > 0) { pickLookup(items[0].tenant_id); return; }
      toast.error('No matching tenant', { description: `Nothing found for "${q}". Try a login email, workspace name, tenant id or sub_… id.` });
      return;
    } catch {
      // network/lookup failure → fall back to treating it as a raw tenant id
    }
    openTimeline(q);
  }, [tlLookup, openTimeline]);

  const exportTimelineCsv = () => {
    if (!tlData?.events?.length) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['date', 'kind', 'title', 'detail', 'status', 'payment_id', 'is_real_charge', 'flag', 'amount_usd', 'credits', 'actor']];
    tlData.events.forEach((e) => rows.push([
      e.date || '', e.kind, e.title, e.detail || '', e.status || '', e.payment_id || '',
      e.is_real_charge ?? '', e.flag || '', e.amount_usd ?? '', e.credits ?? '', e.actor || '',
    ]));
    const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-timeline-${tlData.tenant_id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Timeline exported to CSV');
  };

  const fixMismatch = async () => {
    if (!tlData?.tenant_id) return;
    setTlFixing(true);
    try {
      const res = await governanceAPI.fixBillingTimeline(tlData.tenant_id);
      const acts = (res.data?.actions || []).join(', ');
      toast.success('Mismatch fixed', { description: acts || 'Resolved' });
      const r = await governanceAPI.getBillingTimeline(tlData.tenant_id);
      setTlData(r.data);
      loadIntegrity();
      load();
    } catch (e) {
      toast.error('Fix failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setTlFixing(false);
    }
  };

  const loadIntegrity = useCallback(async () => {
    setIntegrityLoading(true);
    try {
      const res = await governanceAPI.getBillingIntegrity();
      setIntegrity(res.data);
    } catch (e) {
      toast.error('Failed to load billing health', { description: e?.response?.data?.detail || e.message });
    } finally {
      setIntegrityLoading(false);
    }
  }, []);

  const rollbackHealth = async (row) => {
    setBusyHealth(row.tenant_id);
    try {
      await governanceAPI.rollbackBillingIntegrity(row.tenant_id);
      toast.success('Rolled back to a clean free state', {
        description: `${row.tenant_name || row.tenant_id} — no credits changed.`,
      });
      await loadIntegrity();
    } catch (e) {
      toast.error('Rollback failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyHealth(null);
    }
  };

  const relinkHealth = async (row) => {
    setBusyHealth(row.tenant_id);
    try {
      const res = await governanceAPI.relinkBillingIntegrity(row.tenant_id);
      const d = res.data || {};
      toast.success('Relinked to live subscription', {
        description: `${row.tenant_name || row.tenant_id} → ${d.plan_id || 'plan'} restored (PAID)`
          + ((d.voided_invoices || []).length ? ` · voided ${d.voided_invoices.length} phantom invoice(s)` : ''),
      });
      await loadIntegrity();
    } catch (e) {
      toast.error('Relink failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyHealth(null);
    }
  };

  const dismissHealth = async (row) => {
    setBusyHealth(row.tenant_id);
    try {
      await governanceAPI.resolveBillingIntegrity(row.tenant_id);
      toast.success('Flag dismissed');
      await loadIntegrity();
    } catch (e) {
      toast.error('Dismiss failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyHealth(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getBillingDriftReport();
      setReport(res.data);
    } catch (e) {
      toast.error('Failed to load billing drift report', {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadIntegrity(); }, [loadIntegrity]);

  const runBackfillAll = async () => {
    setBackfilling(true);
    try {
      const res = await governanceAPI.backfillRenewals();
      const d = res.data || {};
      toast.success('Renewal self-heal complete', {
        description: `Razorpay re-applied: ${d.razorpay_reapplied ?? 0}/${d.razorpay_checked ?? 0} · Stripe credits backfilled: ${d.credits_backfilled ?? 0}`,
      });
      await load();
    } catch (e) {
      toast.error('Renewal backfill failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBackfilling(false);
    }
  };

  const runBackfillTenant = async (row) => {
    setBusyTenant(row.tenant_id);
    try {
      const res = await governanceAPI.backfillRenewals(row.tenant_id);
      const d = res.data || {};
      toast.success(d.reconciled ? 'Renewal backfilled from live subscription' : 'Nothing to backfill', {
        description: d.reconciled
          ? `Re-applied ${d.subscription_id || ''} (missed credits + GST invoice restored if any)`
          : (d.reason || 'Subscription is not active or has no missed period'),
      });
      await load();
    } catch (e) {
      toast.error('Backfill failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyTenant(null);
    }
  };

  const runFix = async (row) => {
    setBusyTenant(row.tenant_id);
    try {
      const res = await governanceAPI.enforceTenantReconciliation(row.tenant_id);
      const applied = res.data?.applied;
      toast.success(applied ? 'Subscription re-linked & billing status fixed' : 'No change needed', {
        description: applied
          ? `active_subscription_id → ${res.data?.proposed?.active_subscription_id || '—'}`
          : (res.data?.reason || 'Already in sync'),
      });
      await load();
    } catch (e) {
      toast.error('Fix failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyTenant(null);
      setConfirm(null);
    }
  };

  const openVoid = async (row) => {
    setBusyTenant(row.tenant_id);
    try {
      const res = await governanceAPI.voidPhantomInvoices(row.tenant_id, true); // dry-run preview
      setVoidPreview(res.data);
      setConfirm({ type: 'void', row });
    } catch (e) {
      toast.error('Could not scan invoices', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyTenant(null);
    }
  };

  const runVoid = async (row) => {
    setBusyTenant(row.tenant_id);
    try {
      const res = await governanceAPI.voidPhantomInvoices(row.tenant_id, false);
      const n = (res.data?.voided || []).length;
      toast.success(n ? `Voided ${n} phantom invoice(s)` : 'No phantom invoices to void', {
        description: n ? (res.data.voided || []).join(', ') : 'All invoices are backed by real payments.',
      });
    } catch (e) {
      toast.error('Void failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyTenant(null);
      setConfirm(null);
      setVoidPreview(null);
    }
  };

  const changes = report?.changes || [];

  return (
    <div className="space-y-6" data-testid="billing-drift-page">
      <PageHeader
        eyebrow="Billing Ops"
        title="Billing Drift"
        subtitle="Accounts whose stored billing state disagrees with the live payment gateway. Fix re-links the active subscription; it never changes credits."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={runBackfillAll} disabled={backfilling || loading} data-testid="billing-drift-backfill-all">
              {backfilling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Reconcile renewals now
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="billing-drift-refresh">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Tenants scanned', value: report?.total_tenants ?? '—', testid: 'stat-total' },
          { label: 'Drifted accounts', value: report?.would_change ?? '—', testid: 'stat-drift' },
          { label: 'Drift %', value: report ? `${report.change_pct}%` : '—', testid: 'stat-pct' },
          { label: 'Safety valve', value: report?.safety_tripped ? 'TRIPPED' : 'OK', testid: 'stat-safety' },
        ].map((s) => (
          <Card key={s.label} className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
            <CardContent className="p-4">
              <div className="gov-text-tertiary text-xs uppercase tracking-wide">{s.label}</div>
              <div className="gov-text-primary mt-1 text-2xl font-semibold" data-testid={s.testid}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {report?.safety_tripped && (
        <Alert className="border-red-500/40 bg-red-500/10">
          <ShieldAlert className="h-4 w-4 text-red-500" />
          <AlertTitle>Safety valve tripped</AlertTitle>
          <AlertDescription>
            More than {report.max_change_pct}% of tenants would change — bulk enforce is blocked. Review and fix
            individual accounts below.
          </AlertDescription>
        </Alert>
      )}

      {/* Billing Health — half-upgraded / corrupt-billing accounts */}
      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid="billing-health-card">
        <CardHeader>
          <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
            <HeartPulse className="h-5 w-5 text-rose-400" /> Billing Health
          </CardTitle>
          <CardDescription className="gov-text-secondary">
            {integrityLoading ? 'Checking…'
              : `${integrity?.total || 0} account(s) in a half-upgraded/corrupt state (paid flag but demo/TRIAL, no live subscription)`}
          </CardDescription>
          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                value={tlLookup}
                onChange={(e) => setTlLookup(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { viewTimelineFromSearch(); }
                }}
                placeholder="Search by name, email, tenant id or sub_… id"
                className="w-full rounded-md border border-[var(--gov-border)] bg-transparent py-1.5 pl-8 pr-3 text-sm gov-text-primary outline-none focus:border-slate-400"
                data-testid="timeline-lookup-input"
              />
              {lookupResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-surface)] shadow-xl"
                  data-testid="tenant-lookup-results">
                  {lookupResults.map((r) => (
                    <button key={r.tenant_id} onClick={() => pickLookup(r.tenant_id)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-white/5"
                      data-testid={`tenant-lookup-item-${r.tenant_id}`}>
                      <span className="min-w-0">
                        <span className="gov-text-primary block truncate">{r.name}</span>
                        <span className="gov-text-tertiary block truncate text-[11px]">{r.email || r.tenant_id}</span>
                      </span>
                      <span className="gov-text-tertiary shrink-0 text-[11px]">
                        {r.tenant_type || '—'}{r.is_demo ? ' · demo' : ''} · {r.subscription_plan || '—'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="secondary" onClick={viewTimelineFromSearch}
              disabled={!tlLookup.trim()} data-testid="timeline-lookup-btn">
              <History className="mr-1 h-3.5 w-3.5" /> View timeline
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {integrityLoading ? (
            <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (integrity?.flags || []).length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="billing-health-empty">
              <CheckCircle className="h-9 w-9 text-emerald-500" />
              <p className="gov-text-primary font-medium">Billing is healthy</p>
              <p className="gov-text-tertiary text-sm">No half-upgraded or corrupt-billing accounts.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Tenant vs Billing</TableHead>
                  <TableHead>Live sub</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(integrity.flags).map((row) => {
                  const busy = busyHealth === row.tenant_id;
                  const ts = row.tenant_state || {};
                  const bs = row.billing_state || {};
                  return (
                    <TableRow key={row.tenant_id} data-testid={`health-row-${row.tenant_id}`}>
                      <TableCell>
                        <div className="gov-text-primary font-medium">{row.tenant_name}</div>
                        <div className="gov-text-tertiary text-xs">{row.tenant_email || row.tenant_id}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                          <span className={`rounded border px-2 py-0.5 ${statusTone(ts.tenant_type)}`}>
                            {ts.tenant_type || '—'}{ts.is_demo ? ' · demo' : ''}
                          </span>
                          <span className="gov-text-tertiary">plan {ts.subscription_plan || '—'}</span>
                          <span className="gov-text-tertiary">/ billing {bs.plan_id || '—'} ({bs.billing_status || '—'})</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="gov-text-secondary text-xs">
                          {bs.razorpay_subscription_id || bs.stripe_subscription_id || 'none'}
                        </code>
                        {bs.active_subscription_id && !bs.razorpay_subscription_id && !bs.stripe_subscription_id && (
                          <div className="text-[10px] text-amber-500">ghost active_id: {bs.active_subscription_id}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => relinkHealth(row)}
                            disabled={busy}
                            data-testid={`health-relink-${row.tenant_id}`}
                          >
                            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wrench className="mr-1 h-3.5 w-3.5" />}
                            Relink to plan
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => rollbackHealth(row)}
                            disabled={busy}
                            data-testid={`health-rollback-${row.tenant_id}`}
                          >
                            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}
                            Rollback to free
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => dismissHealth(row)}
                            disabled={busy}
                            data-testid={`health-dismiss-${row.tenant_id}`}
                          >
                            <X className="mr-1 h-3.5 w-3.5" /> Dismiss
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openTimeline(row.tenant_id)}
                            data-testid={`health-timeline-${row.tenant_id}`}
                          >
                            <History className="mr-1 h-3.5 w-3.5" /> Timeline
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Credit & Refund Repair — fix overage debt + reconcile WhatsApp refunds */}
      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid="credit-repair-card">
        <CardHeader>
          <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
            <Coins className="h-5 w-5 text-amber-400" /> Credit &amp; Refund Repair
          </CardTitle>
          <CardDescription className="gov-text-secondary">
            Zero a tenant's overage debt (negative balance) and reconcile WhatsApp refunds. Every action is audited.
          </CardDescription>
          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                value={crLookup}
                onChange={(e) => setCrLookup(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (crResults.length > 0) loadCreditStatus(crResults[0].tenant_id);
                    else loadCreditStatus(crLookup.trim());
                  }
                }}
                placeholder="Search by name, email or tenant id"
                className="w-full rounded-md border border-[var(--gov-border)] bg-transparent py-1.5 pl-8 pr-3 text-sm gov-text-primary outline-none focus:border-slate-400"
                data-testid="credit-repair-lookup-input"
              />
              {crResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-surface)] shadow-xl"
                  data-testid="credit-repair-lookup-results">
                  {crResults.map((r) => (
                    <button key={r.tenant_id} onClick={() => loadCreditStatus(r.tenant_id)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-white/5"
                      data-testid={`credit-repair-item-${r.tenant_id}`}>
                      <span className="min-w-0">
                        <span className="gov-text-primary block truncate">{r.name}</span>
                        <span className="gov-text-tertiary block truncate text-[11px]">{r.email || r.tenant_id}</span>
                      </span>
                      <span className="gov-text-tertiary shrink-0 text-[11px]">{r.subscription_plan || '—'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!crStatus ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="credit-repair-empty">
              <Coins className="h-8 w-8 opacity-30" />
              <p className="gov-text-tertiary text-sm">Search a tenant to view and repair their credit balance.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="credit-repair-panel">
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <div className="gov-text-primary font-medium">{crStatus.tenant_name || crStatus.tenant_id}</div>
                  <div className="gov-text-tertiary text-xs">{crStatus.tenant_email || crStatus.tenant_id}</div>
                </div>
                <span className={`rounded border px-2 py-0.5 text-sm ${crStatus.is_overdrawn ? 'border-red-500/40 text-red-400 bg-red-500/10' : 'border-emerald-500/30 text-emerald-500 bg-emerald-500/10'}`}
                  data-testid="credit-repair-balance">
                  {crStatus.credit_balance_credits.toLocaleString()} Cr (${crStatus.credit_balance_usd})
                </span>
                <span className="gov-text-tertiary text-xs">plan {crStatus.plan_id}</span>
                {crStatus.held_usd > 0 && <span className="gov-text-tertiary text-xs">held ${crStatus.held_usd}</span>}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={zeroOverdraft}
                  disabled={crBusy || !crStatus.is_overdrawn}
                  data-testid="credit-repair-zero-btn"
                >
                  {crBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1 h-3.5 w-3.5" />}
                  {crStatus.is_overdrawn ? `Zero overdraft (${Math.abs(crStatus.credit_balance_credits).toLocaleString()} Cr)` : 'No overdraft'}
                </Button>
                <Button size="sm" variant="outline" onClick={previewReconcile} disabled={crBusy} data-testid="credit-repair-scan-btn">
                  {crBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1 h-3.5 w-3.5" />}
                  Scan WhatsApp refunds
                </Button>
              </div>

              {reconcilePreview && (
                <div className="rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-input)] p-3 text-sm" data-testid="reconcile-preview">
                  <div className="flex items-center justify-between py-1">
                    <span className="gov-text-tertiary">Missing refunds (failed but billed)</span>
                    <span className="gov-text-primary">{reconcilePreview.missing_refunds.count} · ${reconcilePreview.missing_refunds.total_usd}</span>
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <span className="gov-text-tertiary">Duplicate refunds (surplus to reverse)</span>
                    <span className="gov-text-primary">{reconcilePreview.duplicate_refunds.wamid_count} · ${reconcilePreview.duplicate_refunds.surplus_usd}</span>
                  </div>
                  {(reconcilePreview.missing_refunds.count === 0 && reconcilePreview.duplicate_refunds.wamid_count === 0) ? (
                    <div className="mt-2 flex items-center gap-2 text-emerald-500">
                      <CheckCircle className="h-4 w-4" /> Refunds are already consistent — nothing to fix.
                    </div>
                  ) : (
                    <div className="mt-2 flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setReconcilePreview(null)}>Cancel</Button>
                      <Button size="sm" onClick={applyReconcile} disabled={crBusy} data-testid="reconcile-apply-btn">
                        {crBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wrench className="mr-1 h-3.5 w-3.5" />}
                        Apply reconciliation
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
        <CardHeader>
          <CardTitle className="gov-text-primary text-lg">Drifted accounts</CardTitle>
          <CardDescription className="gov-text-secondary">
            {loading ? 'Scanning…' : `${changes.length} account(s) need attention`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : changes.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center" data-testid="billing-drift-empty">
              <CheckCircle className="h-10 w-10 text-emerald-500" />
              <p className="gov-text-primary font-medium">No drift detected</p>
              <p className="gov-text-tertiary text-sm">Every non-free account matches its live gateway state.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Stored → Live status</TableHead>
                  <TableHead>Live subscription</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changes.map((row) => {
                  const busy = busyTenant === row.tenant_id;
                  return (
                    <TableRow key={row.tenant_id} data-testid={`drift-row-${row.tenant_id}`}>
                      <TableCell>
                        <div className="gov-text-primary font-medium">{row.tenant_name || 'Unnamed'}</div>
                        <div className="gov-text-tertiary text-xs">{row.tenant_email || row.tenant_id}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline">{row.plan_id}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`rounded border px-2 py-0.5 ${statusTone(row.current?.billing_status)}`}>
                            {row.current?.billing_status || '—'}
                          </span>
                          <span className="gov-text-tertiary">→</span>
                          <span className={`rounded border px-2 py-0.5 ${statusTone(row.proposed?.billing_status)}`}>
                            {row.proposed?.billing_status || '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="gov-text-secondary text-xs">
                          {row.proposed?.active_subscription_id || '—'}
                        </code>
                        {row.current?.active_subscription_id !== row.proposed?.active_subscription_id && (
                          <div className="gov-text-tertiary text-[10px]">
                            was: {row.current?.active_subscription_id || '(none)'}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => setConfirm({ type: 'fix', row })}
                            disabled={busy}
                            data-testid={`fix-btn-${row.tenant_id}`}
                          >
                            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wrench className="mr-1 h-3.5 w-3.5" />}
                            Fix
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => runBackfillTenant(row)}
                            disabled={busy}
                            data-testid={`backfill-btn-${row.tenant_id}`}
                          >
                            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
                            Backfill renewal
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openVoid(row)}
                            disabled={busy}
                            data-testid={`void-btn-${row.tenant_id}`}
                          >
                            <FileX2 className="mr-1 h-3.5 w-3.5" /> Void invoices
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openTimeline(row.tenant_id)}
                            data-testid={`drift-timeline-${row.tenant_id}`}
                          >
                            <History className="mr-1 h-3.5 w-3.5" /> Timeline
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setVoidPreview(null); } }}>
        <DialogContent data-testid="billing-drift-confirm-dialog">
          {confirm?.type === 'fix' ? (
            <>
              <DialogHeader>
                <DialogTitle>Fix billing drift</DialogTitle>
                <DialogDescription>
                  Re-link <strong>{confirm?.row?.tenant_name || confirm?.row?.tenant_id}</strong> to its live
                  subscription and set the correct billing status. This never grants or changes credits.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-input)] p-3 text-sm">
                <div className="flex justify-between"><span className="gov-text-tertiary">Status</span>
                  <span>{confirm?.row?.current?.billing_status} → <strong>{confirm?.row?.proposed?.billing_status}</strong></span></div>
                <div className="mt-1 flex justify-between"><span className="gov-text-tertiary">Active sub</span>
                  <code className="text-xs">{confirm?.row?.proposed?.active_subscription_id}</code></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
                <Button onClick={() => runFix(confirm.row)} disabled={busyTenant === confirm?.row?.tenant_id} data-testid="confirm-fix-btn">
                  {busyTenant === confirm?.row?.tenant_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wrench className="mr-2 h-4 w-4" />}
                  Apply fix
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Void phantom invoices</DialogTitle>
                <DialogDescription>
                  These invoices have no confirmed payment (the backing subscription was never charged). Voiding them
                  is safe — genuinely-paid invoices are automatically kept.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-56 overflow-auto rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-input)] p-3 text-sm">
                {(voidPreview?.candidates_to_void || []).length === 0 ? (
                  <div className="gov-text-tertiary flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" /> No phantom invoices found.
                  </div>
                ) : (
                  (voidPreview.candidates_to_void).map((inv) => (
                    <div key={inv.invoice_number} className="flex items-center justify-between border-b border-[var(--gov-border)] py-1.5 last:border-0">
                      <div>
                        <div className="gov-text-primary">{inv.invoice_number}</div>
                        <code className="gov-text-tertiary text-[10px]">{inv.payment_id || '(no payment id)'}</code>
                      </div>
                      <div className="gov-text-secondary text-xs">₹{inv.total_amount} · {String(inv.invoice_date || '').slice(0, 10)}</div>
                    </div>
                  ))
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setConfirm(null); setVoidPreview(null); }}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={() => runVoid(confirm.row)}
                  disabled={busyTenant === confirm?.row?.tenant_id || (voidPreview?.candidates_to_void || []).length === 0}
                  data-testid="confirm-void-btn"
                >
                  {busyTenant === confirm?.row?.tenant_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileX2 className="mr-2 h-4 w-4" />}
                  Void {(voidPreview?.candidates_to_void || []).length || ''} invoice(s)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-tenant billing timeline */}
      <Dialog open={tlOpen} onOpenChange={setTlOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Billing timeline
              {tlData?.tenant_name ? ` — ${tlData.tenant_name}` : ''}
            </DialogTitle>
            <DialogDescription>
              {tlData?.tenant_email || tlData?.tenant_id || 'Charges, invoices, credit grants & status changes on one line.'}
            </DialogDescription>
          </DialogHeader>

          {tlLoading ? (
            <div className="space-y-3 py-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : tlData ? (
            <div className="flex-1 overflow-y-auto pr-1">
              {/* summary */}
              <div className="mb-4 grid grid-cols-4 gap-2 text-center" data-testid="timeline-summary">
                <div className="rounded-lg border border-[var(--gov-border)] p-2">
                  <div className="text-lg font-semibold gov-text-primary">{tlData.summary.real_charges}</div>
                  <div className="gov-text-tertiary text-[11px]">Real charges</div>
                </div>
                <div className="rounded-lg border border-[var(--gov-border)] p-2">
                  <div className="text-lg font-semibold gov-text-primary">{tlData.summary.paid_invoices}</div>
                  <div className="gov-text-tertiary text-[11px]">Paid invoices</div>
                </div>
                <div className={`rounded-lg border p-2 ${tlData.summary.phantom_invoices ? 'border-amber-500/40' : 'border-[var(--gov-border)]'}`}>
                  <div className={`text-lg font-semibold ${tlData.summary.phantom_invoices ? 'text-amber-500' : 'gov-text-primary'}`}>{tlData.summary.phantom_invoices}</div>
                  <div className="gov-text-tertiary text-[11px]">Phantom</div>
                </div>
                <div className="rounded-lg border border-[var(--gov-border)] p-2">
                  <div className="text-lg font-semibold gov-text-primary">{tlData.summary.credit_grants}</div>
                  <div className="gov-text-tertiary text-[11px]">Credit grants</div>
                </div>
              </div>
              {tlData.summary.mismatch && (
                <Alert className="mb-4 border-amber-500/40 bg-amber-500/10">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <AlertTitle>Charge / invoice mismatch</AlertTitle>
                  <AlertDescription>
                    {tlData.summary.real_charges} real charge(s) but {tlData.summary.paid_invoices} paid invoice(s)
                    {tlData.summary.phantom_invoices ? ` — ${tlData.summary.phantom_invoices} phantom invoice(s) to void.` : '.'}
                  </AlertDescription>
                </Alert>
              )}

              {/* current state chips */}
              <div className="mb-4 flex flex-wrap gap-1.5 text-xs">
                <span className={`rounded border px-2 py-0.5 ${statusTone(tlData.summary.current.billing_status)}`}>
                  billing {tlData.summary.current.billing_status || '—'} · {tlData.summary.current.billing_plan_id || '—'}
                </span>
                <span className="rounded border border-[var(--gov-border)] px-2 py-0.5 gov-text-secondary">
                  tenant {tlData.summary.current.tenant_type || '—'}{tlData.summary.current.is_demo ? ' · demo' : ''} · plan {tlData.summary.current.subscription_plan || '—'}
                </span>
                {tlData.summary.current.paid_through && (
                  <span className="rounded border border-emerald-500/30 px-2 py-0.5 text-emerald-500">
                    paid-through {String(tlData.summary.current.paid_through).slice(0, 10)}
                  </span>
                )}
              </div>

              {/* Invoice ↔ Razorpay charge reconciliation (dry-run first) */}
              {tlData.summary.current.razorpay_subscription_id && (
                <div className="mb-4" data-testid="timeline-reconcile-section">
                  <InvoiceReconcilePanel
                    tenantId={tlData.tenant_id}
                    onApplied={() => { openTimeline(tlData.tenant_id); loadIntegrity(); }}
                  />
                </div>
              )}

              {/* filter chips */}
              <div className="mb-3 flex flex-wrap gap-1.5" data-testid="timeline-filters">
                {[
                  { k: 'all', label: 'All' },
                  { k: 'charge', label: 'Charges' },
                  { k: 'invoice', label: 'Invoices' },
                  { k: 'credit', label: 'Credits' },
                  { k: 'status', label: 'Status' },
                ].map((f) => (
                  <button
                    key={f.k}
                    onClick={() => setTlFilter(f.k)}
                    className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                      tlFilter === f.k
                        ? 'border-sky-500/50 bg-sky-500/15 text-sky-400'
                        : 'border-[var(--gov-border)] gov-text-tertiary hover:text-slate-200'}`}
                    data-testid={`timeline-filter-${f.k}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* events */}
              {tlData.events.filter((e) => tlFilter === 'all' || e.kind === tlFilter).length === 0 ? (
                <p className="gov-text-tertiary py-6 text-center text-sm">No {tlFilter === 'all' ? '' : `${tlFilter} `}events found.</p>
              ) : (
                <ol className="relative border-l border-[var(--gov-border)] pl-4" data-testid="timeline-events">
                  {tlData.events.filter((e) => tlFilter === 'all' || e.kind === tlFilter).map((ev, i) => {
                    const icon = ev.kind === 'invoice' ? <Receipt className="h-3.5 w-3.5" />
                      : ev.kind === 'charge' ? <Receipt className="h-3.5 w-3.5" />
                      : ev.kind === 'credit' ? <Coins className="h-3.5 w-3.5" />
                      : <Activity className="h-3.5 w-3.5" />;
                    const tone = ev.flag ? 'text-amber-500'
                      : ev.kind === 'credit' ? 'text-emerald-500'
                      : (ev.kind === 'invoice' || ev.kind === 'charge') ? 'text-sky-400' : 'text-slate-400';
                    return (
                      <li key={i} className="mb-4 ml-1" data-testid={`timeline-event-${i}`}>
                        <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full bg-[var(--gov-bg-surface)] ${tone}`}>{icon}</span>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="gov-text-primary text-sm font-medium">{ev.title}</span>
                          <span className="gov-text-tertiary text-[11px] whitespace-nowrap">{ev.date ? String(ev.date).slice(0, 10) : '—'}</span>
                        </div>
                        <div className="gov-text-secondary text-xs">{ev.detail}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                          {ev.kind === 'invoice' && (
                            <span className={`rounded px-1.5 py-0.5 ${ev.is_real_charge ? 'bg-sky-500/15 text-sky-400' : 'bg-amber-500/15 text-amber-500'}`}>
                              {ev.is_real_charge ? ev.payment_id : (ev.flag || ev.payment_id || 'no payment id')}
                            </span>
                          )}
                          {ev.kind === 'credit' && typeof ev.credits === 'number' && (
                            <span className={`rounded px-1.5 py-0.5 ${ev.credits >= 0 ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}>
                              {ev.credits >= 0 ? '+' : ''}{ev.credits.toLocaleString()} Cr
                            </span>
                          )}
                          {ev.actor && <span className="gov-text-tertiary">by {ev.actor}</span>}
                          {ev.status && ev.kind === 'invoice' && <span className="gov-text-tertiary">· {ev.status}</span>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTlOpen(false)}>Close</Button>
            {tlData && (
              <Button variant="destructive" onClick={() => setOvOpen(true)} data-testid="timeline-override-btn">
                <SlidersHorizontal className="mr-1 h-4 w-4" /> Manual override
              </Button>
            )}
            <Button variant="secondary" onClick={exportTimelineCsv}
              disabled={!tlData?.events?.length} data-testid="timeline-export-csv">
              <FileX2 className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            {tlData && (tlData.summary?.mismatch || tlData.summary?.phantom_invoices > 0) && (
              <Button onClick={fixMismatch} disabled={tlFixing} data-testid="timeline-fix-btn">
                {tlFixing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wrench className="mr-1 h-4 w-4" />}
                Fix this mismatch
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManualOverrideDialog
        open={ovOpen}
        onOpenChange={setOvOpen}
        tenantId={tlData?.tenant_id}
        onApplied={() => {
          if (tlData?.tenant_id) openTimeline(tlData.tenant_id);
          loadIntegrity();
        }}
      />
    </div>
  );
}
